import { mergeProps as _mergeProps, isVNode as _isVNode, createVNode as _createVNode } from "vue";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { defineComponent, inject, reactive, ref, watch, createVNode, render, provide, getCurrentInstance } from 'vue';
import { omit } from '@/utils';
import { VxeModal, VxeButton } from 'vxe-pc-ui';

// 获取modal注入实例
function _isSlot(s) {
  return typeof s === 'function' || Object.prototype.toString.call(s) === '[object Object]' && !_isVNode(s);
}
export const useComponentModalInstance = () => {
  const raiseUnProvideError = () => {
    throw new Error('组件未在componentModal内');
  };
  return inject('component-modal', {
    /**
     * 是否是modal模式
     * @returns {boolean}
     */
    isModal() {
      return false;
    },
    open() {
      raiseUnProvideError();
    },
    close() {
      raiseUnProvideError();
    },
    confirm(res) {
      raiseUnProvideError(res);
    },
    hide() {
      raiseUnProvideError();
    },
    getBox() {
      return null;
    },
    getPosition() {
      return {
        top: -1,
        left: -1
      };
    },
    setPosition(top, left) {
      raiseUnProvideError(top, left);
    },
    isMaximized() {
      return false;
    },
    zoom() {
      raiseUnProvideError();
    },
    minimize() {
      raiseUnProvideError();
    },
    maximize() {
      raiseUnProvideError();
    },
    revert() {
      raiseUnProvideError();
    },
    /**
     * 获取模式窗口显示的内容组件实例
     */
    'getComponent'() {
      raiseUnProvideError();
    },
    /**
     * 设置模式窗口页脚左则
     * @param footer
     */
    'setLeftFoot'(footer) {
      raiseUnProvideError(footer);
    },
    /**
     * 设置模式窗口页脚右则
     * @param footer
     */
    'setRightFoot'(footer) {
      raiseUnProvideError(footer);
    },
    /**
     * 设置模式窗口页脚按扭列表
     * @param buttons
     */
    'setFootButtons'(buttons) {
      raiseUnProvideError(buttons);
    },
    /**
     * 设置确诊按扭监听器
     * @param listener
     */
    'setConfirmListener'(listener) {},
    /**
     * 设置取消按扭事件监听器
     * @param listener
     */
    'setCancelListener'(listener) {},
    /**
     * 设置模式窗口确认按扭
     * @param options {{ visible?:boolean, text?: string, icon?: string, loading?: boolean, disabled?: boolean }}
     */
    'setConfirmButton'(options) {},
    /**
     * 设置模式窗口取消按扭
     * @param options {{ visible?:boolean, text?: string, icon?: string, loading?: boolean, disabled?: boolean }}
     */
    'setCancelButton'(options) {},
    /**
     * 设置窗口确认和取消按扭
     * @param options {{ showConfirmButton?:boolean, confirmButtonText?:string, confirmButtonIcon?:string,confirmButtonStatus?:string,confirmButtonLoading?:boolean,confirmButtonDisabled?:boolean,showCancelButton?:boolean,cancelButtonText?:string,cancelButtonIcon?:string,cancelButtonStatus?:string,cancelButtonLoading?:boolean,cancelButtonDisabled?:boolean}}
     */
    'setModalOptions'(options) {
      raiseUnProvideError(options);
    }
  });
};
const createComponentModal = (component, componentProps) => {
  return defineComponent({
    props: {
      id: {
        type: String
      },
      title: {
        type: String,
        default: '标题'
      },
      footButtons: {
        type: Array,
        default: () => []
      },
      open: {
        type: Boolean,
        default: false
      },
      mask: {
        type: Boolean,
        default: true
      },
      maskClosable: {
        type: Boolean,
        default: true
      },
      width: {
        type: [String, Number],
        default: '50%'
      },
      height: {
        type: [String, Number]
      },
      minWidth: {
        type: [String, Number]
      },
      minHeight: {
        type: [String, Number]
      },
      escClosable: {
        type: Boolean,
        default: true
      },
      showZoom: {
        type: Boolean,
        default: true
      },
      resize: {
        type: Boolean,
        default: true
      },
      showMaximize: {
        type: Boolean,
        default: true
      },
      showClose: {
        type: Boolean,
        default: true
      },
      lockView: {
        type: Boolean,
        default: true
      },
      showConfirmButton: {
        type: Boolean,
        default: true
      },
      showCancelButton: {
        type: Boolean,
        default: true
      },
      confirmButtonText: {
        type: String,
        default: '确定'
      },
      confirmButtonIcon: {
        type: String,
        default: 'vxe-icon-check'
      },
      confirmButtonStatus: {
        type: String,
        default: 'primary',
        validator(value) {
          return value === null || ['', 'primary', 'info', 'success', 'error', 'warning', 'loading'].indexOf(value) !== -1;
        }
      },
      confirmButtonLoading: {
        type: Boolean,
        default: false
      },
      confirmButtonDisabled: {
        type: Boolean,
        default: false
      },
      cancelButtonText: {
        type: String,
        default: '取消'
      },
      cancelButtonIcon: {
        type: String,
        default: 'vxe-icon-close'
      },
      cancelButtonStatus: {
        type: String,
        default: '',
        validator(value) {
          return value === null || ['', 'primary', 'info', 'success', 'error', 'warning', 'loading'].indexOf(value) !== -1;
        }
      },
      cancelButtonDisabled: {
        type: Boolean,
        default: false
      },
      cancelButtonLoading: {
        type: Boolean,
        default: false
      },
      cancelClosable: {
        type: Boolean,
        default: true
      },
      confirmClosable: {
        type: Boolean,
        default: false
      },
      fullscreen: {
        type: Boolean,
        default: false
      },
      remember: {
        type: Boolean,
        default: false
      },
      storage: {
        type: Boolean,
        default: false
      },
      showFooter: {
        type: Boolean,
        default: true
      },
      showHeader: {
        type: Boolean,
        default: true
      }
    },
    emits: ['visible', 'resize', 'move', 'close', 'hide', 'zoom', 'cancel', 'confirm', 'update:open', 'buttonClick'],
    setup(props, {
      emit,
      expose,
      slots
    }) {
      /**
       * @type {import('vue').Ref<{confirmListener:function():void,cancelListener:function():void,modalButtonClickListener:function(options:{button:any, $event:Event, modal: modalMethods}):void}|undefined>
       */
      const componentRef = ref();
      const modalRef = ref();
      const leftFootSlotRef = ref();
      const rightFootSlotRef = ref();
      const openModal = ref(false);
      const footLeftButtons = ref([]);
      const confirmListenerRef = ref();
      const cancelListenerRef = ref();
      const handleButtonClick = async ({
        button,
        $event
      }) => {
        let result = null;
        if (componentRef.value && typeof componentRef.value.modalButtonClickListener === 'function') {
          result = await componentRef.value.modalButtonClickListener({
            button,
            $event,
            modal: modalMethods
          });
        }
        emit('buttonClick', {
          modal: modalMethods,
          button,
          $event,
          result
        });
      };
      const renderFootLeftButtons = () => {
        return footLeftButtons.value.map((btn, idx) => {
          const {
            content,
            status,
            icon,
            onClick,
            ...more
          } = btn;
          const handleBtnClick = e => {
            if (onClick && typeof onClick === 'function') {
              onClick({
                button: btn,
                $event: e,
                modal: modalMethods
              });
            } else {
              return handleButtonClick({
                button: btn,
                $event: e,
                modal: modalMethods
              });
            }
          };
          return _createVNode(VxeButton, _mergeProps({
            "key": idx,
            "content": content,
            "status": status,
            "icon": icon
          }, more, {
            "onClick": handleBtnClick
          }), _isSlot(content) ? content : {
            default: () => [content]
          });
        });
      };
      watch(() => props.footButtons, val => {
        if (val && Array.isArray(val)) {
          footLeftButtons.value = val;
        } else {
          footLeftButtons.value = [];
        }
      }, {
        immediate: true
      });
      const {
        id,
        title,
        mask,
        maskClosable,
        width,
        height,
        minWidth,
        minHeight,
        escClosable,
        showZoom,
        resize,
        showMaximize,
        showClose,
        lockView,
        showConfirmButton,
        showCancelButton,
        confirmButtonText,
        confirmButtonIcon,
        confirmButtonStatus,
        confirmButtonDisabled,
        confirmButtonLoading,
        cancelButtonText,
        cancelButtonIcon,
        cancelButtonStatus,
        cancelButtonLoading,
        cancelButtonDisabled,
        cancelClosable,
        fullscreen,
        storage,
        remember,
        showFooter,
        showHeader
      } = props;
      const hiddenType = ref('');
      const componentResult = ref();
      const handleConfirm = async ({
        type,
        $event
      }) => {
        hiddenType.value = 'confirm';
        componentResult.value = null;
        const hasPropConfirmListener = confirmListenerRef.value && typeof confirmListenerRef.value === 'function';
        const hasCompConfirmListener = componentRef.value && componentRef.value.confirmListener && typeof componentRef.value.confirmListener === 'function';
        if (hasPropConfirmListener) {
          componentResult.value = await confirmListenerRef.value();
        } else if (hasCompConfirmListener) {
          componentResult.value = await componentRef.value.confirmListener();
        }
        emit('confirm', {
          type,
          $event,
          result: componentResult.value
        });
        if (props.confirmClosable) {
          if (hasPropConfirmListener || hasCompConfirmListener) {
            if (componentResult.value) {
              !!modalRef.value && modalRef.value.close();
            }
          } else {
            !!modalRef.value && modalRef.value.close();
          }
        }
      };
      const handleCancel = async ({
        type,
        $event
      }) => {
        hiddenType.value = type;
        componentResult.value = null;
        if (cancelListenerRef.value && typeof cancelListenerRef.value === 'function') {
          componentResult.value = await cancelListenerRef.value();
        } else if (componentRef.value && typeof componentRef.value.cancelListener === 'function') {
          componentResult.value = await componentRef.value.cancelListener();
        }
        emit('cancel', {
          type,
          $event,
          result: componentResult.value
        });
        if (props.cancelClosable) {
          !!modalRef.value && modalRef.value.close();
        }
      };
      const modalMethods = {
        isModal() {
          return true;
        },
        'instance'() {
          return modalRef.value;
        },
        confirm(res) {
          hiddenType.value = 'confirm';
          componentResult.value = res;
          emit('confirm', {
            type: 'confirm',
            result: res
          });
          if (props.confirmClosable) {
            !!modalRef.value && modalRef.value.close();
          }
        },
        open() {
          !!modalRef.value && modalRef.value.open();
        },
        'hide'() {
          openModal.value = false;
        },
        close() {
          !!modalRef.value && modalRef.value.close();
        },
        getBox() {
          if (modalRef.value) {
            return modalRef.value.getBox();
          }
          return null;
        },
        getPosition() {
          if (modalRef.value) {
            return modalRef.value.getPosition();
          }
          return {
            top: -1,
            left: -1
          };
        },
        setPosition(top, left) {
          if (modalRef.value) {
            modalRef.value.setPosition(top, left);
          }
        },
        isMaximized() {
          if (modalRef.value) {
            return modalRef.value.isMaximized();
          }
          return false;
        },
        zoom() {
          if (modalRef.value) {
            return modalRef.value.zoom();
          }
        },
        minimize() {
          !!modalRef.value && modalRef.value.minimize();
        },
        maximize() {
          !!modalRef.value && modalRef.value.maximize();
        },
        revert() {
          !!modalRef.value && modalRef.value.revert();
        },
        'getComponent'() {
          return componentRef.value;
        },
        'setLeftFoot'(footer) {
          if (typeof footer === 'function') {
            leftFootSlotRef.value = footer;
          } else {
            leftFootSlotRef.value = () => footer;
          }
        },
        'setRightFoot'(footer) {
          if (typeof footer === 'function') {
            rightFootSlotRef.value = footer;
          } else {
            rightFootSlotRef.value = () => footer;
          }
        },
        'setFootButtons'(buttons) {
          if (Array.isArray(buttons)) {
            footLeftButtons.value = buttons;
          }
        },
        'setConfirmListener'(listener) {
          confirmListenerRef.value = listener;
        },
        'setCancelListener'(listener) {
          cancelListenerRef.value = listener;
        }
      };
      const modalProps = reactive({
        id,
        title,
        mask,
        maskClosable,
        width,
        height,
        minHeight,
        minWidth,
        escClosable,
        showZoom,
        resize,
        showMaximize,
        showClose,
        lockView,
        showConfirmButton,
        showCancelButton,
        confirmButtonText,
        cancelButtonText,
        confirmClosable: false,
        cancelClosable,
        transfer: false,
        destroyOnClose: true,
        storage: !!props.id && storage,
        fullscreen,
        remember,
        showHeader,
        showFooter
      });

      // 模式窗口扩展属性
      const modalExtraProps = reactive({
        confirmButtonIcon: confirmButtonIcon || 'vxe-icon-check',
        confirmButtonStatus,
        confirmButtonLoading: confirmButtonLoading || false,
        confirmButtonDisabled: confirmButtonDisabled || false,
        cancelButtonIcon: cancelButtonIcon || 'vxe-icon-close',
        cancelButtonStatus,
        cancelButtonLoading: cancelButtonLoading || false,
        cancelButtonDisabled: cancelButtonDisabled || false
      });
      const modalSlots = reactive({
        ...slots,
        'leftfoot'() {
          if (slots.leftfoot) {
            return slots.leftfoot({
              modal: modalMethods,
              component: componentRef
            });
          } else if (leftFootSlotRef.value && typeof leftFootSlotRef.value === 'function') {
            return leftFootSlotRef.value({
              modal: modalMethods,
              component: componentRef
            });
          } else if (slots.leftfoot) {
            return slots.leftfoot;
          } else if (Array.isArray(footLeftButtons.value) && footLeftButtons.value.length > 0) {
            return renderFootLeftButtons({
              modal: modalMethods,
              component: componentRef
            });
          }
        },
        'rightfoot'() {
          if (slots.rightfoot) {
            return slots.rightfoot({
              modal: modalMethods,
              component: componentRef
            });
          } else if (rightFootSlotRef.value && typeof rightFootSlotRef.value === 'function') {
            return rightFootSlotRef.value({
              modal: modalMethods,
              component: componentRef
            });
          } else if (slots.rightfoot) {
            return slots.rightfoot;
          } else if (modalProps.showCancelButton || modalProps.showConfirmButton) {
            return _createVNode("div", null, [!!modalProps.showConfirmButton && _createVNode(VxeButton, {
              "status": modalExtraProps.confirmButtonStatus || 'primary',
              "content": modalProps.confirmButtonText,
              "icon": modalExtraProps.confirmButtonIcon,
              "loading": modalExtraProps.confirmButtonLoading,
              "disabled": modalExtraProps.confirmButtonDisabled,
              "onClick": e => handleConfirm({
                type: 'confirm',
                $event: e
              })
            }, null), !!modalProps.showCancelButton && _createVNode(VxeButton, {
              "status": modalExtraProps.cancelButtonStatus || 'error',
              "content": modalProps.cancelButtonText,
              "icon": modalExtraProps.cancelButtonIcon,
              "loading": modalExtraProps.cancelButtonLoading,
              "disabled": modalExtraProps.cancelButtonDisabled,
              "onClick": e => handleCancel({
                type: 'cancel',
                $event: e
              })
            }, null)]);
          }
        }
      });
      Object.assign(modalMethods, {
        'setConfirmButton'(options) {
          const {
            visible,
            text,
            status,
            loading,
            disabled,
            icon
          } = options || {};
          Object.assign(modalProps, omit({
            showConfirmButton: visible,
            confirmButtonText: text
          }));
          Object.assign(modalExtraProps, omit({
            confirmButtonIcon: icon,
            confirmButtonStatus: status,
            confirmButtonLoading: loading,
            confirmButtonDisabled: disabled
          }));
        },
        'setCancelButton'(options) {
          const {
            visible,
            text,
            status,
            loading,
            disabled,
            icon
          } = options || {};
          Object.assign(modalProps, omit({
            showCancelButton: visible,
            cancelButtonText: text
          }));
          Object.assign(modalExtraProps, omit({
            cancelButtonIcon: icon,
            cancelButtonStatus: status,
            cancelButtonLoading: loading,
            cancelButtonDisabled: disabled
          }));
        },
        'setModalOptions'(options) {
          const {
            showConfirmButton,
            confirmButtonText,
            confirmButtonIcon,
            confirmButtonStatus,
            confirmButtonLoading,
            confirmButtonDisabled,
            showCancelButton,
            cancelButtonText,
            cancelButtonIcon,
            cancelButtonStatus,
            cancelButtonLoading,
            cancelButtonDisabled
          } = options;
          Object.assign(modalExtraProps, omit({
            confirmButtonIcon,
            confirmButtonStatus,
            confirmButtonLoading,
            confirmButtonDisabled,
            cancelButtonIcon,
            cancelButtonStatus,
            cancelButtonLoading,
            cancelButtonDisabled
          }));
          Object.assign(modalProps, omit({
            showCancelButton,
            showConfirmButton,
            confirmButtonText,
            cancelButtonText
          }));
        }
      });
      const modalEvents = {
        onConfirm: handleConfirm,
        onCancel: handleCancel,
        onClose({
          type,
          $event
        }) {
          emit('close', {
            type,
            modal: modalMethods,
            $event
          });
        },
        'onResize'({
          type,
          $event
        }) {
          emit('resize', {
            type,
            modal: modalMethods,
            $event
          });
        },
        'onZoom'({
          type,
          $event
        }) {
          emit('zoom', {
            type,
            modal: modalMethods,
            $event
          });
        },
        'onMove'({
          type,
          $event
        }) {
          emit('move', {
            type,
            modal: modalMethods,
            $event
          });
        },
        'onShow'({
          type
        }) {
          hiddenType.value = type;
          emit('update:open', true);
          emit('visible', {
            type,
            visible: true,
            modal: modalMethods
          });
        },
        onHide({
          type
        }) {
          let resultType = type;
          if (['model', 'close'].indexOf(type) !== -1 && ['confirm', 'cancel'].indexOf(hiddenType.value) !== -1) {
            resultType = hiddenType.value;
          }
          emit('update:open', false);
          emit('hide', {
            type: resultType,
            visible: false,
            modal: modalMethods,
            result: componentResult.value
          });
        }
      };
      watch(() => props.open, val => {
        openModal.value = val;
      }, {
        immediate: true
      });
      watch(() => [props.showCancelButton, props.showConfirmButton], attrs => {
        const [showCancelButton, showConfirmButton] = attrs;
        Object.assign(modalProps, {
          showCancelButton,
          showConfirmButton
        });
      }, {
        immediate: true
      });
      watch(() => [props.width, props.minWidth], val => {
        const [w, mw] = val;
        Object.assign(modalProps, {
          width: w,
          minWidth: mw
        });
      }, {
        immediate: true
      });
      watch(() => [props.height, props.minHeight], val => {
        const [h, mh] = val;
        Object.assign(modalProps, {
          height: h,
          minHeight: mh
        });
      }, {
        immediate: true
      });
      expose(modalMethods);
      provide('component-modal', modalMethods);
      return () => {
        return _createVNode(VxeModal, _mergeProps({
          "ref": modalRef,
          "modelValue": openModal.value
        }, modalProps, modalEvents, {
          "slots": {
            ...modalSlots
          }
        }), {
          default: () => [_createVNode(component, _mergeProps({
            "ref": componentRef
          }, componentProps), null)]
        });
      };
    }
  });
};
const defaultEditorModalOptions = {
  fullscreen: false,
  mask: true,
  maskClosable: true,
  width: '50%',
  height: null,
  minHeight: null,
  minWidth: null,
  escClosable: true,
  showZoom: true,
  resize: true,
  showMaximize: true,
  showMinimize: true,
  showClose: true,
  showFooter: true,
  showHeader: true,
  lockView: true,
  showConfirmButton: true,
  confirmButtonText: '确定',
  showCancelButton: true,
  cancelButtonText: '取消',
  confirmButtonStatus: 'primary',
  confirmButtonLoading: false,
  confirmButtonDisabled: false,
  cancelButtonStatus: 'error',
  cancelButtonLoading: false,
  cancelButtonDisabled: false,
  confirmClosable: true,
  cancelClosable: true,
  footButtons: [],
  remember: false,
  storage: false
};
export const useComponentModal = () => {
  const {
    appContext
  } = getCurrentInstance();
  const openComponent = (title, component, params = {}, options = {
    id: '',
    // 传入id,将自动保持组件位置状态
    mask: true,
    maskClosable: true,
    width: '50%',
    height: null,
    minWidth: null,
    minHeight: null,
    escClosable: true,
    showZoom: true,
    resize: true,
    showMaximize: true,
    showMinimize: true,
    showClose: true,
    showHeader: true,
    showFooter: true,
    lockView: true,
    showConfirmButton: true,
    confirmButtonText: '确定',
    showCancelButton: true,
    cancelButtonText: '取消',
    confirmButtonStatus: 'primary',
    cancelButtonStatus: 'error',
    confirmButtonLoading: false,
    cancelButtonLoading: false,
    confirmButtonDisabled: false,
    cancelButtonDisabled: false,
    cancelClosable: true,
    confirmClosable: false,
    footButtons: [],
    onClose: undefined,
    fullscreen: false,
    remember: false,
    storage: false,
    slots: {}
  }) => {
    return new Promise(resolve => {
      const componentModal = createComponentModal(component, params);
      let container;
      const handleOnHidden = (vNode, {
        modal
      }) => {
        !!options.onClose && options.onClose(modal);
        if (vNode?.el && vNode.el.parentNode) {
          vNode.el.parentNode.removeChild(vNode.el);
        }
        if (container) {
          document.body.removeChild(container);
          container = null;
        }
      };
      const {
        slots = {},
        ...attrs
      } = options;
      const modalNode = createVNode(componentModal, {
        ...attrs,
        title,
        open: true,
        onHide: ({
          type,
          $event,
          modal,
          result
        }) => {
          resolve({
            type,
            $event,
            modal,
            result
          });
          handleOnHidden(modalNode, {
            modal
          });
        },
        onConfirm: ({
          result,
          modal,
          $event
        }) => {
          if (!options.confirmClosable) {
            resolve({
              type: 'confirm',
              modal,
              result,
              $event
            });
          }
        },
        onCancel: ({
          modal,
          $event
        }) => {
          if (!options.cancelClosable) {
            // 点取消不自动关闭时,触发事件
            resolve({
              type: 'cancel',
              modal,
              $event
            });
          }
        }
      }, slots);
      modalNode.appContext = appContext;
      container = document.createElement('div');
      render(modalNode, container);
      document.body.appendChild(container);
    });
  };

  // openEditor 打开数据编辑器
  const openEditor = async (title, editor, params = {}, options = {
    id: '',
    // 转入id值将保持组件状态
    mask: true,
    maskClosable: true,
    width: '50%',
    height: null,
    minHeight: null,
    minWidth: null,
    escClosable: true,
    showZoom: true,
    resize: true,
    showMaximize: true,
    showMinimize: true,
    showClose: true,
    showFooter: true,
    lockView: true,
    showConfirmButton: true,
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    confirmButtonStatus: 'primary',
    cancelButtonStatus: 'error',
    confirmButtonLoading: false,
    cancelButtonLoading: false,
    confirmButtonDisabled: false,
    cancelButtonDisabled: false,
    confirmClosable: true,
    cancelClosable: true,
    footButtons: [],
    fullscreen: false,
    remember: false,
    storage: false
  }) => {
    const {
      type,
      result
    } = await openComponent(title, editor, params, {
      ...Object.assign({
        ...defaultEditorModalOptions
      }, options)
    });
    if (type === 'confirm') {
      return Promise.resolve(result);
    } else {
      return Promise.resolve(false);
    }
  };
  return {
    openComponent,
    openEditor
  };
};